/**
 * The shared auth shell (spec M0-F3 §8.1).
 *
 * Measured from `discovery/ui-reference/first-look/login-otp.png` (the header band, the
 * column width and the field/button metrics) and `login-form.json` (the field order and the
 * copy). The wordmark is **ours**, never monday's (ADR-001 §4).
 */
import { Wordmark } from '@elizmonday/ui';
import type { ReactNode } from 'react';
import './auth.css';

export interface AuthShellProps {
  title: string;
  subtitle?: ReactNode;
  children?: ReactNode;
  /** The "Can't log in? Visit our help center" line at the bottom of every auth screen. */
  footer?: ReactNode;
  testId?: string;
}

export function AuthShell({ title, subtitle, children, footer, testId }: AuthShellProps) {
  return (
    <div className="ez-auth" data-testid={testId ?? 'auth-shell'}>
      <header className="ez-auth__header">
        <Wordmark height={32} data-testid="auth-wordmark" />
      </header>
      <main className="ez-auth__main">
        <div className="ez-auth__column">
          <h1 className="ez-auth__title" data-testid="auth-title">
            {title}
          </h1>
          {subtitle === undefined ? null : (
            <p className="ez-auth__subtitle" data-testid="auth-subtitle">
              {subtitle}
            </p>
          )}
          {children}
          <p className="ez-auth__footer" data-testid="auth-footer">
            {footer ?? (
              <>
                {"Can't log in? "}
                <a className="ez-auth__link" href="/help">
                  Visit our help center
                </a>
              </>
            )}
          </p>
        </div>
      </main>
    </div>
  );
}

/** One inline error line, in the single error style every auth screen uses (§8.2). */
export function AuthError({ children }: { children: ReactNode }) {
  return children === null || children === undefined || children === '' ? null : (
    <p className="ez-auth__error" role="alert" data-testid="auth-error">
      {children}
    </p>
  );
}
